// The two-factor deadline (decision 20, QA-093): for someone the company requires it of, on every
// screen until it's on: "Set up two-factor within 7 days: 5 days left.", then, past them, only setup
// is open (the sign-in prototype's words). After the owner resets it for someone it isn't required
// of: "Your two-factor was reset by …. Set it up again?", once, with "Not now" (QA-183).
// Read once a page load (and again after ten minutes).

import { useEffect, useState } from 'react';
import { client } from '../api.ts';
import { deadlineBanner, type TwoFactorState } from './signin-model.ts';
import './signin.css';

let loaded: { at: number; state: Promise<TwoFactorState | null> } | null = null;
export function twoFactorState(fresh = false): Promise<TwoFactorState | null> {
  if (fresh || !loaded || Date.now() - loaded.at > 600_000)
    loaded = { at: Date.now(), state: client.me<TwoFactorState>('GET', '/me/2fa').catch(() => null) };
  return loaded.state;
}

/** Your sign-in just read it: the banner, and the hold past the deadline, follow at once. */
export function primeTwoFactorState(s: TwoFactorState): void {
  loaded = { at: Date.now(), state: Promise.resolve(s) };
}

export function TwoFactorBanner({ onSetUp, owner = false }: { onSetUp: () => void; owner?: boolean }) {
  const [state, setState] = useState<TwoFactorState | null>(null);
  useEffect(() => {
    let live = true;
    void twoFactorState().then((s) => live && setState(s));
    return () => {
      live = false;
    };
  }, []);
  const b = state ? deadlineBanner(state) : null;
  const prompt = state?.resetPrompt;
  if (!b && prompt)
    return (
      <div className="account-banner warn" role="status">
        <p>{prompt.message}</p>
        <button className="btn primary" onClick={onSetUp}>
          Set it up
        </button>
        <button
          className="btn quiet"
          onClick={() => {
            const seen = { ...state, resetPrompt: null };
            primeTwoFactorState(seen);
            setState(seen);
            void client.me('POST', '/me/2fa/reset-seen', {}).catch(() => undefined);
          }}
        >
          Not now
        </button>
      </div>
    );
  if (!b) return null;
  return (
    <div className={`account-banner ${b.tone}`} role={b.tone === 'bad' ? 'alert' : 'status'}>
      <p>
        <b>{b.text}</b>{' '}
        {/* The owner never chose it: say plainly why it's theirs (QA-330). Hidden on a phone. */}
        <span className="detail">
          {owner && !state?.blocked
            ? 'You’re the owner, so your sign-in guards the whole company.'
            : b.detail}
        </span>
      </p>
      {/* past the deadline every held place says "Set up two-factor" itself: one action, not two */}
      {state?.blocked ? null : (
        <button className="btn primary" onClick={onSetUp}>
          Set up two-factor
        </button>
      )}
    </div>
  );
}
